import React, { useState, useEffect, useCallback, useRef } from "react";
import {
    Box, Flex, Text, Button, Table, Thead, Tbody, Tr, Th, Td, IconButton, Input,
    HStack, useToast, FormControl, FormLabel, Select, SimpleGrid, AlertDialog,
    AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent,
    AlertDialogOverlay, Spinner, Badge, InputGroup, InputLeftElement, VStack, Tag,
    Image, ButtonGroup, Tooltip, Card, CardBody, useBreakpointValue, Link
} from "@chakra-ui/react";
import { SearchIcon, RepeatIcon, UnlockIcon, ExternalLinkIcon } from "@chakra-ui/icons";
import { LayoutGrid, List as ListIcon } from "lucide-react";
import { listarImagensBloqueadas, desbloquearImagem } from "../services/api";
import { useDebounce } from "../hooks/useDebounce";
import ScrollToTop from "../components/ScrollToTop";

const initialFiltros = {
    q: "",
    escopo: "",
};

const ImagensBloqueadas = () => {
    const toast = useToast();
    const cancelRef = useRef();
    const isMobile = useBreakpointValue({ base: true, md: false });

    const [bloqueios, setBloqueios] = useState([]);
    const [loading, setLoading] = useState(true);
    const [paginationMeta, setPaginationMeta] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [perPage, setPerPage] = useState(30);
    const [filtros, setFiltros] = useState(initialFiltros);
    const [viewMode, setViewMode] = useState("table");

    const debouncedBusca = useDebounce(filtros.q, 400);

    const [confirmAlert, setConfirmAlert] = useState({ isOpen: false, id: null });
    const [processing, setProcessing] = useState(false);

    useEffect(() => {
        if (isMobile) setViewMode("card");
    }, [isMobile]);

    const fetchBloqueios = useCallback(async (opts = {}) => {
        const silencioso = !!opts.silencioso;
        if (!silencioso) setLoading(true);
        const params = { page: currentPage, per_page: perPage };
        if (filtros.escopo) params.escopo = filtros.escopo;
        if (debouncedBusca) params.q = debouncedBusca;

        try {
            const res = await listarImagensBloqueadas(params);
            setBloqueios(res.data.data || []);
            setPaginationMeta({
                current_page: res.data.current_page,
                last_page: res.data.last_page,
                total: res.data.total,
            });
        } catch (e) {
            toast({ title: "Erro ao carregar bloqueios", status: "error", duration: 3000 });
        }
        if (!silencioso) setLoading(false);
    }, [currentPage, perPage, filtros.escopo, debouncedBusca, toast]);

    useEffect(() => { fetchBloqueios(); }, [fetchBloqueios]);
    useEffect(() => { setCurrentPage(1); }, [filtros.escopo, debouncedBusca]);

    const handleFiltro = (campo, valor) => {
        setFiltros(prev => ({ ...prev, [campo]: valor }));
    };
    const limparFiltros = () => setFiltros(initialFiltros);

    const abrirConfirm = (id) => setConfirmAlert({ isOpen: true, id });
    const fecharConfirm = () => setConfirmAlert({ isOpen: false, id: null });

    const executarDesbloqueio = async () => {
        setProcessing(true);
        try {
            await desbloquearImagem(confirmAlert.id);
            toast({ title: "Imagem desbloqueada", status: "success", duration: 2500 });
            fecharConfirm();
            fetchBloqueios({ silencioso: true });
        } catch (e) {
            toast({
                title: "Erro ao desbloquear",
                description: e.response?.data?.error || "Falha na operação",
                status: "error", duration: 4000,
            });
        }
        setProcessing(false);
    };

    const renderThumb = (b, size = "48px") => (
        b.url_referencia ? (
            <Image
                src={b.url_referencia}
                w={size} h={size} objectFit="contain"
                bg="gray.50" borderRadius="sm"
                fallbackSrc="https://via.placeholder.com/48?text=?" />
        ) : (
            <Box w={size} h={size} bg="gray.100" borderRadius="sm" />
        )
    );

    return (
        <Box ml={{ base: 0, md: "64px" }} pt="56px" minH="100vh" bg="gray.50">
            <ScrollToTop />
            <Box maxW="1280px" mx="auto" py={{ base: 4, md: 6 }} px={{ base: 3, md: 8 }}>
                <Flex justify="space-between" align={{ base: "flex-start", md: "center" }}
                    mb={4} direction={{ base: "column", md: "row" }} gap={2}>
                    <Box>
                        <Text fontSize={{ base: "xl", md: "2xl" }} fontWeight="bold">Imagens Bloqueadas</Text>
                        <Text fontSize="sm" color="gray.600">
                            Imagens proibidas de serem cadastradas. Removendo desta lista volta a permitir o cadastro.
                        </Text>
                    </Box>
                    <HStack>
                        <ButtonGroup size="sm" isAttached variant="outline">
                            <Tooltip label="Visualização em tabela" hasArrow>
                                <IconButton icon={<ListIcon size={16} />} aria-label="Tabela"
                                    colorScheme={viewMode === "table" ? "purple" : "gray"}
                                    variant={viewMode === "table" ? "solid" : "outline"}
                                    onClick={() => setViewMode("table")} />
                            </Tooltip>
                            <Tooltip label="Visualização em cards" hasArrow>
                                <IconButton icon={<LayoutGrid size={16} />} aria-label="Cards"
                                    colorScheme={viewMode === "card" ? "purple" : "gray"}
                                    variant={viewMode === "card" ? "solid" : "outline"}
                                    onClick={() => setViewMode("card")} />
                            </Tooltip>
                        </ButtonGroup>
                        <Button leftIcon={<RepeatIcon />} onClick={() => fetchBloqueios()} size="sm">Atualizar</Button>
                    </HStack>
                </Flex>

                <Box bg="white" p={{ base: 3, md: 4 }} borderRadius="md" boxShadow="sm" mb={4}>
                    <FormControl mb={3}>
                        <FormLabel fontSize="xs" mb={1}>Buscar por nome do produto, código global, motivo ou hash MD5</FormLabel>
                        <InputGroup size="sm">
                            <InputLeftElement pointerEvents="none">
                                <SearchIcon color="gray.400" />
                            </InputLeftElement>
                            <Input value={filtros.q}
                                onChange={(e) => handleFiltro("q", e.target.value)}
                                placeholder="Digite para buscar..." />
                        </InputGroup>
                    </FormControl>
                    <SimpleGrid columns={{ base: 1, sm: 3 }} spacing={3}>
                        <FormControl>
                            <FormLabel fontSize="xs" mb={1}>Escopo</FormLabel>
                            <Select size="sm" value={filtros.escopo}
                                onChange={(e) => handleFiltro("escopo", e.target.value)}>
                                <option value="">Todos</option>
                                <option value="produto">Por produto</option>
                                <option value="global">Globais</option>
                            </Select>
                        </FormControl>
                        <FormControl display="flex" alignItems="flex-end">
                            <Button size="xs" variant="outline" onClick={limparFiltros}>Limpar filtros</Button>
                        </FormControl>
                    </SimpleGrid>
                </Box>

                <Box bg="white" borderRadius="md" boxShadow="sm" overflow="hidden">
                    {loading ? (
                        <Flex justify="center" align="center" h="300px">
                            <Spinner size="xl" color="orange.500" />
                        </Flex>
                    ) : bloqueios.length === 0 ? (
                        <Flex justify="center" align="center" h="200px" px={4}>
                            <Text color="gray.500" textAlign="center">
                                Nenhuma imagem bloqueada com esses filtros.
                            </Text>
                        </Flex>
                    ) : viewMode === "table" ? (
                        <Box overflowX="auto">
                            <Table size="sm" variant="simple">
                                <Thead bg="gray.100">
                                    <Tr>
                                        <Th>Imagem</Th>
                                        <Th>Escopo</Th>
                                        <Th>Produto</Th>
                                        <Th>Hash MD5</Th>
                                        <Th>Motivo</Th>
                                        <Th>Bloqueado por</Th>
                                        <Th>Quando</Th>
                                        <Th>Ações</Th>
                                    </Tr>
                                </Thead>
                                <Tbody>
                                    {bloqueios.map((b) => (
                                        <Tr key={b.id} _hover={{ bg: "gray.50" }}>
                                            <Td>{renderThumb(b)}</Td>
                                            <Td>
                                                {b.produto_id ? (
                                                    <Tag size="sm" colorScheme="purple">Produto</Tag>
                                                ) : (
                                                    <Tag size="sm" colorScheme="red">Global</Tag>
                                                )}
                                            </Td>
                                            <Td maxW="280px">
                                                {b.produto_id ? (
                                                    <VStack align="flex-start" spacing={0.5} fontSize="sm">
                                                        <HStack spacing={1.5}>
                                                            <Badge colorScheme="gray" fontSize="xs" fontFamily="mono">
                                                                #{b.produto_id}
                                                            </Badge>
                                                            <Tooltip label="Ver produto">
                                                                <Link href={`/produtos?id=${b.produto_id}`} isExternal>
                                                                    <ExternalLinkIcon boxSize={3} color="blue.400" />
                                                                </Link>
                                                            </Tooltip>
                                                        </HStack>
                                                        <Text fontWeight="semibold" noOfLines={2}>
                                                            {b.produto_nome || `#${b.produto_id}`}
                                                        </Text>
                                                        <HStack spacing={2} fontSize="xs" color="gray.600" flexWrap="wrap">
                                                            {b.produto_codigo_global && (
                                                                <Tag size="sm" variant="subtle" colorScheme="blue">
                                                                    {b.produto_codigo_global}
                                                                </Tag>
                                                            )}
                                                            {b.produto_marca && (
                                                                <Tag size="sm" variant="subtle" colorScheme="purple">
                                                                    {b.produto_marca}
                                                                </Tag>
                                                            )}
                                                        </HStack>
                                                    </VStack>
                                                ) : (
                                                    <Text fontSize="sm" color="gray.500">— (todos os produtos)</Text>
                                                )}
                                            </Td>
                                            <Td>
                                                <Text fontSize="xs" fontFamily="mono" color="gray.600">
                                                    {b.hash_md5?.substring(0, 12)}...
                                                </Text>
                                            </Td>
                                            <Td maxW="220px">
                                                <Text fontSize="xs" noOfLines={2}>{b.motivo || "-"}</Text>
                                            </Td>
                                            <Td fontSize="xs">{b.bloqueado_por_nome || "-"}</Td>
                                            <Td fontSize="xs" whiteSpace="nowrap">
                                                {b.created_at ? new Date(b.created_at).toLocaleDateString("pt-BR") : "-"}
                                            </Td>
                                            <Td>
                                                <IconButton size="xs" colorScheme="green" icon={<UnlockIcon />}
                                                    aria-label="Desbloquear"
                                                    title="Desbloquear"
                                                    onClick={() => abrirConfirm(b.id)} />
                                            </Td>
                                        </Tr>
                                    ))}
                                </Tbody>
                            </Table>
                        </Box>
                    ) : (
                        <SimpleGrid columns={{ base: 2, sm: 3, md: 4, lg: 5 }} spacing={3} p={3}>
                            {bloqueios.map((b) => (
                                <Card key={b.id} variant="outline" overflow="hidden">
                                    <Box position="relative" pb="100%" bg="gray.50">
                                        {b.url_referencia ? (
                                            <Image
                                                src={b.url_referencia}
                                                position="absolute" top={0} left={0}
                                                w="100%" h="100%" objectFit="contain"
                                                loading="lazy"
                                                fallbackSrc="https://via.placeholder.com/200?text=?" />
                                        ) : (
                                            <Flex position="absolute" top={0} left={0} w="100%" h="100%"
                                                align="center" justify="center">
                                                <Text fontSize="xs" color="gray.400">Sem imagem</Text>
                                            </Flex>
                                        )}
                                        <Badge
                                            colorScheme={b.produto_id ? "purple" : "red"}
                                            position="absolute" top={1} right={1} fontSize="9px">
                                            {b.produto_id ? "Produto" : "Global"}
                                        </Badge>
                                    </Box>
                                    <CardBody p={2}>
                                        <VStack align="stretch" spacing={1}>
                                            {b.produto_id ? (
                                                <>
                                                    <HStack spacing={1.5}>
                                                        <Badge colorScheme="gray" fontSize="9px" fontFamily="mono">
                                                            #{b.produto_id}
                                                        </Badge>
                                                        <Tooltip label="Ver produto">
                                                            <Link href={`/produtos?id=${b.produto_id}`} isExternal>
                                                                <ExternalLinkIcon boxSize={3} color="blue.400" />
                                                            </Link>
                                                        </Tooltip>
                                                    </HStack>
                                                    <Text fontSize="xs" fontWeight="semibold" noOfLines={2}>
                                                        {b.produto_nome || `#${b.produto_id}`}
                                                    </Text>
                                                    {b.produto_codigo_global && (
                                                        <Tag size="sm" variant="subtle" colorScheme="blue" alignSelf="flex-start">
                                                            {b.produto_codigo_global}
                                                        </Tag>
                                                    )}
                                                </>
                                            ) : (
                                                <Text fontSize="xs" color="gray.500">Todos os produtos</Text>
                                            )}
                                            {b.motivo && (
                                                <Text fontSize="9px" color="gray.500" noOfLines={2}>{b.motivo}</Text>
                                            )}
                                            <Text fontSize="9px" color="gray.400">
                                                {b.created_at ? new Date(b.created_at).toLocaleDateString("pt-BR") : ""}
                                                {b.bloqueado_por_nome ? ` · ${b.bloqueado_por_nome}` : ""}
                                            </Text>
                                            <Button size="xs" colorScheme="green" leftIcon={<UnlockIcon />}
                                                onClick={() => abrirConfirm(b.id)}>
                                                Desbloquear
                                            </Button>
                                        </VStack>
                                    </CardBody>
                                </Card>
                            ))}
                        </SimpleGrid>
                    )}
                </Box>

                {paginationMeta && paginationMeta.last_page > 1 && (
                    <Flex justify="space-between" align="center" mt={4} wrap="wrap" gap={2}>
                        <HStack>
                            <Text fontSize="sm" color="gray.600">
                                Página {paginationMeta.current_page} de {paginationMeta.last_page} · {paginationMeta.total} bloqueios
                            </Text>
                            <Select size="xs" maxW="100px" value={perPage}
                                onChange={(e) => { setPerPage(Number(e.target.value)); setCurrentPage(1); }}>
                                <option value={15}>15 / pág</option>
                                <option value={30}>30 / pág</option>
                                <option value={60}>60 / pág</option>
                            </Select>
                        </HStack>
                        <HStack>
                            <Button size="sm" isDisabled={currentPage === 1}
                                onClick={() => setCurrentPage(p => p - 1)}>Anterior</Button>
                            <Button size="sm" isDisabled={currentPage === paginationMeta.last_page}
                                onClick={() => setCurrentPage(p => p + 1)}>Próxima</Button>
                        </HStack>
                    </Flex>
                )}
            </Box>

            <AlertDialog isOpen={confirmAlert.isOpen} leastDestructiveRef={cancelRef}
                onClose={fecharConfirm} isCentered>
                <AlertDialogOverlay>
                    <AlertDialogContent mx={3}>
                        <AlertDialogHeader fontSize="md">Desbloquear imagem</AlertDialogHeader>
                        <AlertDialogBody>
                            <Text fontSize="sm">
                                Tem certeza que deseja remover este bloqueio? A imagem voltará a ser permitida
                                no sistema.
                            </Text>
                        </AlertDialogBody>
                        <AlertDialogFooter>
                            <Button ref={cancelRef} onClick={fecharConfirm} isDisabled={processing} size="sm">
                                Cancelar
                            </Button>
                            <Button colorScheme="green" onClick={executarDesbloqueio}
                                ml={3} isLoading={processing} size="sm" leftIcon={<UnlockIcon />}>
                                Desbloquear
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialogOverlay>
            </AlertDialog>
        </Box>
    );
};

export default ImagensBloqueadas;
